import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatRelative } from '../../notifications/utils/formatDateTime';
import {
  ITEM_BODY_FONT_SIZE,
  ITEM_META_FONT_SIZE,
  ITEM_PADDING_X,
  ITEM_PADDING_Y,
  ITEM_TITLE_FONT_SIZE,
  UNREAD_ACCENT_WIDTH,
} from '../designTokens';
import type { InboxItem } from '../types';

export interface NotificationItemProps {
  item: InboxItem;
  read: boolean;
  onDismiss: () => void;
  /** Fired when the title's link is followed — the panel closes behind it. */
  onNavigate: () => void;
  dismissDisabled?: boolean;
}

/**
 * One row of the panel (IAN-26).
 *
 * Unread is carried by TWO signals, never colour alone (AC-29, SPEC "Item"): a
 * left accent border plus a stronger title weight — the second because colour
 * alone fails at both ends of the contrast range and in dark mode.
 *
 * An item with no `deepLink` renders its title as plain text rather than a
 * dead click target (SPEC "an item with no deep link is not a dead click
 * target"). `deepLink` is always an absolute URL (the backend builds it
 * against its own configured origin), so a plain anchor is correct here —
 * there is no relative path to hand to a router `Link`.
 *
 * The dismiss control is revealed on hover, and stays visible whenever the
 * device has no hover to reveal it on (SPEC "Actions") — `@media (hover:
 * none)` targets exactly that, rather than a viewport width, because a wide
 * touch tablet has the same problem a phone does.
 */
export function NotificationItem({
  item,
  read,
  onDismiss,
  onNavigate,
  dismissDisabled,
}: NotificationItemProps) {
  return (
    <Box
      component="li"
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: ITEM_PADDING_Y,
        pl: ITEM_PADDING_X,
        pr: 1,
        borderLeft: `${UNREAD_ACCENT_WIDTH}px solid`,
        borderBottom: '1px solid',
        // Each side's colour is set on its own, and the four-sided `borderColor`
        // shorthand must never come back. MUI folds it into the `border-left`
        // shorthand it emits and drops `borderLeftColor` from the output
        // altogether, so the accent silently rendered as the divider colour in
        // BOTH states — the unread marker was dead styling that no unit test
        // could see, because the props were right and only the emitted CSS was
        // wrong. Caught in the browser; keep the sides separate.
        borderTopColor: 'divider',
        borderRightColor: 'divider',
        borderBottomColor: 'divider',
        borderLeftColor: read ? 'transparent' : 'primary.main',
        '&:last-of-type': { borderBottomWidth: 0 },
        '&:hover .notification-item-dismiss': { opacity: 1 },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {item.deepLink ? (
          <Box
            component="a"
            href={item.deepLink}
            onClick={onNavigate}
            sx={{
              display: 'block',
              fontSize: ITEM_TITLE_FONT_SIZE,
              fontWeight: read ? 500 : 700,
              color: 'text.primary',
              textDecoration: 'none',
              '&:hover, &:focus-visible': { textDecoration: 'underline' },
            }}
          >
            {item.title}
          </Box>
        ) : (
          <Typography
            sx={{ fontSize: ITEM_TITLE_FONT_SIZE, fontWeight: read ? 500 : 700, color: 'text.primary' }}
          >
            {item.title}
          </Typography>
        )}
        <Typography sx={{ fontSize: ITEM_BODY_FONT_SIZE, color: 'text.secondary', mt: 0.25 }}>
          {item.body}
        </Typography>
        <Typography sx={{ fontSize: ITEM_META_FONT_SIZE, color: 'text.disabled', mt: 0.5 }}>
          {formatRelative(item.createdAt)}
        </Typography>
      </Box>
      <IconButton
        className="notification-item-dismiss"
        aria-label={`Dismiss "${item.title}"`}
        size="small"
        onClick={onDismiss}
        disabled={dismissDisabled}
        sx={{
          flexShrink: 0,
          opacity: 0,
          '@media (hover: none)': { opacity: 1 },
          '&:focus-visible': { opacity: 1 },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

export default NotificationItem;
